import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api, { appointmentsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, 
  Calendar, 
  MapPin, 
  Phone, 
  Mail, 
  Clock,
  CalendarDays,
  ChevronRight,
  Sun
} from 'lucide-react';
import { format, parseISO, isToday, isFuture, endOfYear } from 'date-fns';
import { de } from 'date-fns/locale';

export default function AdminTodayAppointments() {
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [futureAppointments, setFutureAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFuture, setShowFuture] = useState(false);

  useEffect(() => {
    fetchAppointments();
  }, []);

  const fetchAppointments = async () => {
    try {
      const [todayRes, allRes] = await Promise.all([
        appointmentsAPI.getTodayAppointments(),
        api.get('/appointments')
      ]);
      
      setTodayAppointments(todayRes.data || []);
      
      // Filter future appointments
      const future = (allRes.data || [])
        .filter(apt => {
          const aptDate = parseISO(apt.date);
          return isFuture(aptDate) && !isToday(aptDate) && aptDate <= endOfYear(new Date());
        })
        .sort((a, b) => new Date(a.date) - new Date(b.date));
      
      setFutureAppointments(future);
    } catch (error) {
      console.error('Failed to fetch appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'confirmed':
        return <Badge className="bg-green-100 text-green-800">Bestätigt</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800">Ausstehend</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">{status}</Badge>;
    }
  };

  const AppointmentCard = ({ apt, isToday = false }) => (
    <Link to={`/admin/termine?date=${apt.date}`} key={apt.id}>
      <Card className={`hover:shadow-lg transition-shadow cursor-pointer ${isToday ? 'border-l-4 border-[#2E2A88]' : ''}`}>
        <CardContent className="p-4 md:p-6">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className={`w-16 h-16 md:w-20 md:h-20 ${isToday ? 'bg-[#2E2A88]' : 'bg-[#9B1B30]'} text-white rounded-lg flex flex-col items-center justify-center flex-shrink-0`}>
                <span className="font-barlow font-bold text-xl md:text-2xl">
                  {isToday ? apt.time.split(':')[0] : format(parseISO(apt.date), 'd')}
                </span>
                <span className="font-manrope text-xs uppercase">
                  {isToday ? 'Uhr' : format(parseISO(apt.date), 'MMM', { locale: de })}
                </span>
              </div>
              
              <div className="flex-1 min-w-0">
                <p className="font-barlow font-bold text-lg md:text-xl text-[#1A1A1A]">
                  {isToday ? `Heute um ${apt.time} Uhr` : `${format(parseISO(apt.date), 'EEEE', { locale: de })} um ${apt.time} Uhr`}
                </p>
                <p className="font-manrope text-gray-700 text-base md:text-lg mt-1">
                  {apt.customer_name}
                </p>
                
                <div className="mt-3 space-y-1">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{apt.street} {apt.house_number}, {apt.postal_code} {apt.city}</span>
                  </div>
                  {apt.phone && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Phone className="w-4 h-4 flex-shrink-0" />
                      <span>{apt.phone}</span>
                    </div>
                  )}
                  {apt.email && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Mail className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{apt.email}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2 md:flex-col md:items-end">
              {isToday ? (
                <Badge className="bg-blue-100 text-blue-800">Heute</Badge>
              ) : (
                <Badge className="bg-gray-200 text-gray-700">
                  {format(parseISO(apt.date), 'd. MMM', { locale: de })}
                </Badge>
              )}
              {getStatusBadge(apt.status)}
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9B1B30]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-4 md:py-8" data-testid="today-appointments-page">
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-2 md:gap-4">
            <Link to="/admin/dashboard">
              <Button variant="ghost" size="sm" className="px-2 md:px-4">
                <ArrowLeft className="w-4 h-4 md:mr-2" />
                <span className="hidden md:inline">Zurück</span>
              </Button>
            </Link>
            <h1 className="font-barlow font-bold text-xl md:text-2xl text-[#1A1A1A]">
              <Sun className="w-6 h-6 inline mr-2 text-yellow-500" />
              Heutige Termine
            </h1>
          </div>
          <Button 
            variant={showFuture ? "default" : "outline"}
            onClick={() => setShowFuture(!showFuture)}
            className="w-full sm:w-auto"
          >
            <CalendarDays className="w-4 h-4 mr-2" />
            {showFuture ? 'Nur heute' : `Zukünftige anzeigen (${futureAppointments.length})`}
          </Button>
        </div>

        {/* Today's Appointments */}
        <div className="mb-8">
          <h2 className="font-barlow font-bold text-lg text-[#2E2A88] mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            HEUTE - {format(new Date(), 'd. MMMM yyyy', { locale: de })} ({todayAppointments.length} Termine)
          </h2>
          
          <div className="space-y-4">
            {todayAppointments.length > 0 ? (
              todayAppointments.map((apt) => (
                <AppointmentCard key={apt.id} apt={apt} isToday={true} />
              ))
            ) : (
              <Card>
                <CardContent className="p-8 text-center">
                  <Sun className="w-16 h-16 text-yellow-300 mx-auto mb-4" />
                  <p className="font-manrope text-gray-500 text-lg">Keine Termine heute</p>
                  <p className="font-manrope text-gray-400 text-sm mt-2">Genießen Sie Ihren freien Tag!</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Future Appointments */}
        {showFuture && futureAppointments.length > 0 && (
          <div>
            <h2 className="font-barlow font-bold text-lg text-[#9B1B30] mb-4 flex items-center gap-2">
              <CalendarDays className="w-5 h-5" />
              KOMMENDE TERMINE BIS JAHRESENDE ({futureAppointments.length})
            </h2>
            
            <div className="space-y-4">
              {futureAppointments.map((apt) => (
                <AppointmentCard key={apt.id} apt={apt} isToday={false} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
